import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, FilePdf, FileImage, CheckCircle, XCircle, Spinner, Trash } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";

const MIME_ICON = (name) => (name || "").toLowerCase().endsWith(".pdf") ? FilePdf : FileImage;
const humanSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const CONCURRENCY = 3;
const ANALYZE_TIMEOUT_MS = 180000; // 3 min per file — Gemini can be slow with big PDFs

export default function AdminUploadDocuments() {
  const [queue, setQueue] = useState([]); // {file, status: pending|uploading|ok|error, message?, startedAt?, elapsed?}
  const [running, setRunning] = useState(false);
  const [concurrency, setConcurrency] = useState(CONCURRENCY);
  const [tick, setTick] = useState(0);
  const inputRef = useRef(null);

  // Tick every second while running so per-row elapsed time updates
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const valid = arr.filter((f) => {
      const ok = f.type === "application/pdf" || f.type.startsWith("image/") || f.name.toLowerCase().endsWith(".pdf");
      if (!ok) toast.error(`"${f.name}" no es PDF ni imagen — ignorado`);
      return ok;
    });
    setQueue((q) => [...q, ...valid.map((f) => ({ file: f, status: "pending" }))]);
  };

  const removeItem = (idx) => setQueue((q) => q.filter((_, i) => i !== idx));
  const clearAll = () => setQueue([]);

  const uploadAll = async () => {
    if (queue.length === 0 || running) return;
    setRunning(true);

    // Snapshot pending indexes and process them in a worker-pool of `concurrency` size
    const pendingIndexes = queue
      .map((it, idx) => (it.status === "pending" ? idx : -1))
      .filter((i) => i !== -1);

    const processOne = async (idx) => {
      const current = queue[idx];
      if (!current) return;
      const startedAt = Date.now();
      setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: "uploading", startedAt } : it)));
      const fd = new FormData();
      fd.append("file", current.file);
      try {
        const { data } = await api.post("/invoices/analyze", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: ANALYZE_TIMEOUT_MS,
        });
        setQueue((q) => q.map((it, i) => (i === idx ? {
          ...it,
          status: "ok",
          message: data.supplier_created
            ? `Proveedor "${data.supplier.name}" creado · ${data.invoice.document_type} guardada`
            : `Guardada en "${data.supplier.name}" (${data.invoice.document_type})`,
          invoiceId: data.invoice.id,
          elapsed: Math.round((Date.now() - startedAt) / 1000),
        } : it)));
      } catch (e) {
        setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: "error", message: formatApiError(e), elapsed: Math.round((Date.now() - startedAt) / 1000) } : it)));
      }
    };

    // Simple concurrency-limited pool: each worker consumes the shared queue.
    let cursor = 0;
    const worker = async () => {
      while (cursor < pendingIndexes.length) {
        const my = cursor++;
        await processOne(pendingIndexes[my]);
      }
    };
    const workers = Array.from({ length: Math.max(1, Math.min(concurrency, pendingIndexes.length)) }, worker);
    await Promise.all(workers);

    setRunning(false);
    toast.success(`Proceso completado (${pendingIndexes.length} fichero${pendingIndexes.length === 1 ? "" : "s"})`);
  };

  const okCount = queue.filter((q) => q.status === "ok").length;
  const errCount = queue.filter((q) => q.status === "error").length;
  const pendingCount = queue.filter((q) => q.status === "pending").length;
  const uploadingCount = queue.filter((q) => q.status === "uploading").length;
  const doneCount = okCount + errCount;
  const progressPct = queue.length ? Math.round((doneCount / queue.length) * 100) : 0;
  void tick; // used to force re-render each second while running

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="label-cap mb-1">proveedores</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">Cargar documentos</h1>
        <p className="text-[var(--text-secondary)] mt-2">Sube varios PDFs o imágenes a la vez. Cada fichero se procesa como una factura/albarán y se guarda automáticamente.</p>
      </div>

      <div
        className="card-flat p-8 border-2 border-dashed border-[var(--border)] rounded-2xl text-center cursor-pointer hover:border-[var(--primary)] transition"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        data-testid="upload-dropzone"
      >
        <UploadSimple size={40} weight="duotone" className="mx-auto text-[var(--primary)] mb-3" />
        <div className="font-display font-semibold text-lg">Arrastra archivos aquí o pulsa para seleccionar</div>
        <div className="text-sm text-[var(--text-secondary)] mt-1">Acepta PDF e imágenes · varios ficheros a la vez</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          data-testid="upload-file-input"
          className="hidden"
        />
      </div>

      {queue.length > 0 && (
        <div className="mt-6 card-flat p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-sm">
              <span className="font-display font-bold">{queue.length}</span>
              <span className="text-[var(--text-secondary)]"> ficheros · </span>
              <span className="text-emerald-700 font-mono">{okCount} OK</span>
              <span className="text-[var(--text-secondary)]"> · </span>
              <span className="text-red-700 font-mono">{errCount} error</span>
              <span className="text-[var(--text-secondary)]"> · </span>
              <span className="font-mono">{pendingCount} pendientes</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-[var(--text-secondary)] font-display font-semibold" htmlFor="concurrency-select">
                En paralelo
              </label>
              <select
                id="concurrency-select"
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.target.value))}
                disabled={running}
                data-testid="upload-concurrency"
                className="px-2 py-1 rounded-full border border-[var(--border)] text-xs font-display font-semibold bg-white disabled:opacity-40"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={clearAll}
                disabled={running}
                data-testid="upload-clear"
                className="px-3 py-1.5 rounded-full border border-[var(--border)] text-xs font-display font-semibold hover:bg-[var(--bg)] disabled:opacity-40"
              >
                Limpiar
              </button>
              <button
                onClick={uploadAll}
                disabled={running || pendingCount === 0}
                data-testid="upload-start"
                className="px-4 py-1.5 rounded-full btn-primary text-xs font-display font-bold disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {running ? <><Spinner size={14} className="animate-spin" /> Procesando…</> : <>Procesar {pendingCount} fichero(s)</>}
              </button>
            </div>
          </div>
          {running && (
            <div className="mb-4" data-testid="upload-progress-bar">
              <div className="flex items-center justify-between text-xs text-[var(--text-secondary)] mb-1 font-display">
                <span>
                  <Spinner size={12} weight="bold" className="inline animate-spin text-[var(--primary)] mr-1" />
                  Procesando · {doneCount}/{queue.length} · {uploadingCount} en curso
                </span>
                <span className="font-mono">{progressPct}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-[var(--bg)] overflow-hidden">
                <div
                  className="h-full bg-[var(--primary)] transition-[width] duration-500 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
          <ul className="space-y-2">
            {queue.map((it, idx) => {
              const Icon = MIME_ICON(it.file.name);
              const uploading = it.status === "uploading";
              const rowClass = uploading
                ? "flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--primary)] bg-[var(--primary)]/5 shadow-sm"
                : "flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-white";
              const elapsedNow = uploading && it.startedAt ? Math.round((Date.now() - it.startedAt) / 1000) : it.elapsed;
              return (
                <li key={`${it.file.name}-${it.file.size}-${it.file.lastModified || idx}`} data-testid={`upload-item-${idx}`} className={rowClass}>
                  <Icon size={22} weight="duotone" className="text-[var(--primary)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{it.file.name}</div>
                    <div className="text-xs text-[var(--text-secondary)] flex items-center gap-2 flex-wrap">
                      <span>{humanSize(it.file.size)}</span>
                      {uploading && <span className="text-[var(--primary)] font-mono">· Analizando con IA… {elapsedNow ? `${elapsedNow}s` : ""}</span>}
                      {it.message && <span className={it.status === "error" ? "text-red-700" : "text-emerald-700"}>· {it.message}</span>}
                      {!uploading && it.elapsed != null && <span className="font-mono opacity-70">· {it.elapsed}s</span>}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {it.status === "pending" && <span className="text-xs text-[var(--text-secondary)] font-mono">Pendiente</span>}
                    {uploading && <Spinner size={22} className="animate-spin text-[var(--primary)]" />}
                    {it.status === "ok" && <CheckCircle size={22} weight="fill" className="text-emerald-600" />}
                    {it.status === "error" && <XCircle size={22} weight="fill" className="text-red-600" />}
                  </div>
                  {it.status === "pending" && !running && (
                    <button
                      onClick={() => removeItem(idx)}
                      data-testid={`upload-remove-${idx}`}
                      className="w-8 h-8 rounded-lg border border-[var(--border)] flex items-center justify-center hover:bg-[var(--danger)] hover:text-white"
                      title="Quitar de la lista"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
